/**
 * Transcription API — batch SAS upload URL, jobs, listing, detail, cancel, and vocabulary.
 *
 * Live streaming uses `WSS /api/v1/transcription/live` (not covered here).
 */
import { apiRequest } from "../client";
import type {
  CreateTranscriptionJobRequest,
  CreateTranscriptionJobResponse,
  GetTranscriptionUploadUrlRequest,
  GetTranscriptionUploadUrlResponse,
  ListTranscriptionJobsParams,
  ListTranscriptionJobsResponse,
  GetTranscriptionJobResponse,
  TranscriptionVocabularyResponse,
} from "./types";

/**
 * Requests a short-lived SAS URL and canonical `audio_uri` for direct upload to the transcription-audio container (`200`).
 */
export async function getUploadUrl(
  data: GetTranscriptionUploadUrlRequest
): Promise<GetTranscriptionUploadUrlResponse> {
  return await apiRequest<GetTranscriptionUploadUrlResponse>({
    method: "POST",
    url: "/transcription/upload-url",
    data,
  });
}

/**
 * PUTs raw audio bytes to Azure Blob using the SAS URL from {@link getUploadUrl}.
 * Uses `fetch` (not the API client) so no auth headers are sent to the storage host.
 */
export async function putAudioToSasUrl(
  sasUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  const res = await fetch(sasUrl, {
    method: "PUT",
    body: file,
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": contentType,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Audio upload to storage failed (${res.status}): ${text || res.statusText}`.trim()
    );
  }
}

/**
 * Enqueues an asynchronous transcription job for remote audio (`202 Accepted`).
 *
 * @param data - Audio URI and optional diarization / provider options
 * @returns Created job ids, queued state, and runner task id
 */
export async function createJob(
  data: CreateTranscriptionJobRequest
): Promise<CreateTranscriptionJobResponse> {
  return await apiRequest<CreateTranscriptionJobResponse>({
    method: "POST",
    url: "/transcription/jobs",
    data,
  });
}

/**
 * Lists transcription sessions for the current tenant (newest first), with pagination.
 *
 * @param params - Page, page size, and optional mode filter
 */
export async function listJobs(
  params?: ListTranscriptionJobsParams
): Promise<ListTranscriptionJobsResponse> {
  return await apiRequest<ListTranscriptionJobsResponse>({
    method: "GET",
    url: "/transcription/jobs",
    params,
  });
}

/**
 * Fetches a single job/session including transcript utterances when available.
 *
 * @param jobId - Job id (same as session id in the current API)
 */
export async function getJob(jobId: string): Promise<GetTranscriptionJobResponse> {
  return await apiRequest<GetTranscriptionJobResponse>({
    method: "GET",
    url: `/transcription/jobs/${jobId}`,
  });
}

/**
 * Requests cancellation of a queued or running job (`204` on success).
 *
 * @param jobId - Job id to cancel
 */
export async function cancelJob(jobId: string): Promise<void> {
  await apiRequest<void>({
    method: "DELETE",
    url: `/transcription/jobs/${jobId}`,
  });
}

/**
 * Returns the bundled reference glossary (admin-only; may `403` for non-admin callers).
 */
export async function getVocabulary(): Promise<TranscriptionVocabularyResponse> {
  return await apiRequest<TranscriptionVocabularyResponse>({
    method: "GET",
    url: "/transcription/vocabulary",
  });
}
